"use client"

import React, { useState, useCallback, useMemo } from "react"
import { Document, Page, pdfjs } from "react-pdf"
import { motion, AnimatePresence } from "motion/react"
import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"

import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  RotateRight,
  Download,
  Maximize,
  Minimize,
  FileCopy,
} from "@mui/icons-material"
import { Spinner } from "@/components/ui/spinner"

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString()

interface PDFToolbarProps {
  pageNumber: number
  numPages: number
  pageInput: string
  scale: number
  loading: boolean
  isFullscreen: boolean
  onPageInputChange: (value: string) => void
  onPageInputSubmit: () => void
  onPrevPage: () => void
  onNextPage: () => void
  onZoomIn: () => void
  onZoomOut: () => void
  onResetZoom: () => void
  onRotate: () => void
  onToggleFullscreen: () => void
  onDownload: () => void
}

function PDFToolbar({
  pageNumber,
  numPages,
  pageInput,
  scale,
  loading,
  isFullscreen,
  onPageInputChange,
  onPageInputSubmit,
  onPrevPage,
  onNextPage,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onRotate,
  onToggleFullscreen,
  onDownload,
}: PDFToolbarProps) {
  return (
    <div className=" bg-neutral-800">
      <div className="p-4">
        <div className="flex items-center justify-between gap-4">
          {/* Navigation Controls */}
          <div className="flex items-center gap-2">
            <Button variant="default" size="sm" onClick={onPrevPage} disabled={pageNumber <= 1 || loading}>
              <ChevronLeft className="w-5 h-5" />
            </Button>

            <div className="flex items-center gap-2">
              <Input
                type="text"
                value={pageInput}
                onChange={(e) => onPageInputChange(e.target.value)}
                onBlur={onPageInputSubmit}
                onKeyDown={(e) => e.key === "Enter" && onPageInputSubmit()}
                className="w-16 text-center rounded-full"
                disabled={loading}
              />
              <span className="text-neutral-400">of {numPages || 0}</span>
            </div>

            <Button variant="default" size="sm" onClick={onNextPage} disabled={pageNumber >= numPages || loading}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>

          <div className="h-6 w-px bg-neutral-600" />

          {/* Zoom Controls */}
          <div className="flex items-center gap-2">
            <Button variant="default" size="sm" onClick={onZoomOut} disabled={scale <= 0.5 || loading}>
              <ZoomOut className="w-5 h-5" />
            </Button>

            <div className="min-w-[60px] text-center justify-center text-neutral-400">
              {Math.round(scale * 100)}%
            </div>

            <Button variant="default" size="sm" onClick={onZoomIn} disabled={scale >= 3.0 || loading}>
              <ZoomIn className="w-5 h-5" />
            </Button>

            <Button variant="default" size="sm" onClick={onResetZoom} disabled={loading}>
              Fit
            </Button>
          </div>

          <div className="h-6 w-px bg-neutral-600" />

          {/* Additional Controls */}
          <div className="flex items-center gap-2">
            <Button variant="default" size="sm" onClick={onRotate} disabled={loading}>
              <RotateRight className="w-5 h-5" />
            </Button>

            <Button variant="default" size="sm" onClick={onToggleFullscreen} disabled={loading}>
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </Button>

            <Button variant="default" size="sm" onClick={onDownload} disabled={loading}>
              <Download className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

interface PDFReaderProps {
  pdfUrl?: string
}

const PDFReader = React.memo(function PDFReader({ pdfUrl = "/placeholder.pdf" }: PDFReaderProps) {
  const [numPages, setNumPages] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [scale, setScale] = useState<number>(1.0)
  const [rotation, setRotation] = useState<number>(0)
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string>("")
  const [pageInput, setPageInput] = useState<string>("1")

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setLoading(false)
    setError("")
  }, [])

  const onDocumentLoadError = useCallback((error: Error) => {
    setError("Failed to load PDF document")
    setLoading(false)
    console.error("PDF load error:", error)
  }, [])

  const goToPrevPage = useCallback(() => {
    if (pageNumber > 1) {
      const newPage = pageNumber - 1
      setPageNumber(newPage)
      setPageInput(newPage.toString())
    }
  }, [pageNumber])

  const goToNextPage = useCallback(() => {
    if (pageNumber < numPages) {
      const newPage = pageNumber + 1
      setPageNumber(newPage)
      setPageInput(newPage.toString())
    }
  }, [pageNumber, numPages])

  const handlePageInputChange = useCallback((value: string) => {
    setPageInput(value)
  }, [])

  const handlePageInputSubmit = useCallback(() => {
    const page = Number.parseInt(pageInput, 10)
    if (page >= 1 && page <= numPages) {
      setPageNumber(page)
    } else {
      setPageInput(pageNumber.toString())
    }
  }, [pageInput, numPages, pageNumber])

  const zoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.25, 3.0))
  }, [])

  const zoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 0.25, 0.5))
  }, [])

  const resetZoom = useCallback(() => {
    setScale(1.0)
  }, [])

  const rotate = useCallback(() => {
    setRotation((prev) => (prev + 90) % 360)
  }, [])

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev)
  }, [])

  const downloadPDF = useCallback(() => {
    const link = document.createElement("a")
    link.href = pdfUrl
    link.download = "document.pdf"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [pdfUrl])

  // This is used to force a re-render of the motion.div when the loading or error state changes
  const motionKey = useMemo(() => {
    if (loading) return 'loading'
    if (error) return 'error'
    return `document`
  }, [loading, error])

  return (
    <div className={`flex flex-col bg-neutral-800 w-full h-full overflow-hidden ${isFullscreen ? "fixed inset-0 z-50" : ""}`}>
      <PDFToolbar
        pageNumber={pageNumber}
        numPages={numPages}
        pageInput={pageInput}
        scale={scale}
        loading={loading}
        isFullscreen={isFullscreen}
        onPageInputChange={handlePageInputChange}
        onPageInputSubmit={handlePageInputSubmit}
        onPrevPage={goToPrevPage}
        onNextPage={goToNextPage}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onResetZoom={resetZoom}
        onRotate={rotate}
        onToggleFullscreen={toggleFullscreen}
        onDownload={downloadPDF}
      />

      {/* PDF Viewer */}
      <div className="flex-1 w-full h-full bg-neutral-800 overflow-auto overflow-y-auto scrollbar-thin scrollbar-track-neutral-800 scrollbar-thumb-neutral-600 hover:scrollbar-thumb-neutral-500">
        <div className="min-h-full p-4 flex justify-center items-center">
          <AnimatePresence mode="wait">
          <motion.div
            key={motionKey}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="block"
          >
            {loading && (
              <div className="flex items-center justify-center h-96 w-96">
                <div className="flex flex-col items-center gap-4">
                  <Spinner aria-label="Loading PDF" className="size-10 text-white" />
                </div>
              </div>
            )}

            {error && (
              <div className="flex items-center justify-center h-96 w-96">
                <div className="flex flex-col items-center gap-4 text-center">
                  <FileCopy className="w-14 h-14 text-neutral-400" />
                  <div>
                    <p className="text-lg font-medium text-white">Unable to load PDF</p>
                    <p className="text-neutral-400">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {!error && !loading && (
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading=""
                className="shadow-lg"
              >
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  rotate={rotation}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="border border-neutral-700 bg-white overflow-hidden"
                />
              </Document>
            )}

            {!error && loading && (
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading=""
                className="hidden"
              />
            )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Status Bar */}
      <div className="border-t border-neutral-800 bg-neutral-800">
        <div className="p-2">
          <div className="flex items-center justify-between text-neutral-400">
            <div className="flex items-center gap-4">
              <span>
                Page {pageNumber} of {numPages}
              </span>
              <span>Zoom: {Math.round(scale * 100)}%</span>
              {rotation > 0 && <span>Rotated: {rotation}°</span>}
            </div>
            <div className="flex items-center gap-2">
              <FileCopy className="w-5 h-5" />
              <span>PDF Document</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
});

export default PDFReader;